// USER-LANG-DATE-ORDER-1 — ONE localized display-date helper for the whole client.
//
// Why this exists: month/day NAMES already follow the UI language (core/i18n.ts switches the
// dayjs/moment locale), but the ORDER did not — call sites used fixed English patterns such as
// `.format("MMM D, YYYY")`, so Italian read "nov 1, 2026". Every display date now goes through
// `Intl.DateTimeFormat(getUiLocale(), …)`, which applies each language's own order and
// punctuation (CLDR): en "Nov 1, 2026", it "1 nov 2026", de "1. Nov. 2026", pl "1 lis 2026",
// sk/cs "1. 11. 2026".
//
// Why Intl and not dayjs LocalizedFormat (`ll`, `lll`): dayjs has no "day + month without year"
// token, and its sk/cs `ll` ("1. nov 2026") differs from the CLDR short date the owner asked for.
// One mechanism for every style keeps the app consistent.
//
// DISPLAY ONLY. Never use this for API payloads, `YYYY-MM-DD` keys, filenames or sorting — those
// stay on fixed dayjs patterns. Time is always 24 h ("14:05"), as the fixed "HH:mm" was before.
//
// Parsing goes through dayjs so a date-only string ("2026-11-01") stays LOCAL midnight exactly as
// `dayjs(x).format(…)` treated it (`new Date("2026-11-01")` would be UTC midnight).
import dayjs from "dayjs";
import { getUiLocale } from "./relativeTime";

export type UiDateStyle =
  | "date" // Nov 1, 2026
  | "dayMonth" // Nov 1
  | "dateTime" // Nov 1, 2026, 14:05
  | "dayMonthTime" // Nov 1, 14:05
  | "time" // 14:05
  | "weekdayDate" // Sun, Nov 1, 2026
  | "longWeekdayDate" // Sunday, Nov 1, 2026
  | "longWeekdayLongDate"; // Sunday, November 1, 2026

/** Anything the app passes to dayjs/moment today: ISO string, epoch ms, Date, dayjs or moment. */
export type UiDateInput = string | number | Date | { toDate(): Date } | null | undefined;

const TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
const DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
const DAY_MONTH: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };

const STYLE_OPTIONS: Record<UiDateStyle, Intl.DateTimeFormatOptions> = {
  date: DATE,
  dayMonth: DAY_MONTH,
  dateTime: { ...DATE, ...TIME },
  dayMonthTime: { ...DAY_MONTH, ...TIME },
  time: TIME,
  weekdayDate: { weekday: "short", ...DATE },
  longWeekdayDate: { weekday: "long", ...DATE },
  longWeekdayLongDate: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
};

const formatters = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (style: UiDateStyle, locale: string): Intl.DateTimeFormat => {
  const key = `${locale}|${style}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, STYLE_OPTIONS[style]);
    formatters.set(key, formatter);
  }
  return formatter;
};

const toDate = (input: UiDateInput): Date | null => {
  if (input === null || input === undefined || input === "") return null;
  const raw = typeof input === "object" && !(input instanceof Date) ? input.toDate() : input;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Display a date in the active UI language's natural order. Returns "" for a missing or
 * unparseable date (callers keep their own "—" fallbacks). `locale` is for tests only.
 */
export const formatUiDate = (input: UiDateInput, style: UiDateStyle = "date", locale: string = getUiLocale()): string => {
  const date = toDate(input);
  return date ? formatterFor(style, locale).format(date) : "";
};

/**
 * A date span: same day → one date; same year → "Nov 1 – Nov 5, 2026" (start without year);
 * otherwise both full dates. `separator` keeps each caller's existing dash.
 */
export const formatUiDateRange = (
  start: UiDateInput,
  end: UiDateInput,
  separator = " – ",
  locale: string = getUiLocale()
): string => {
  const from = toDate(start);
  const to = toDate(end);
  if (!from || !to) return formatUiDate(from || to, "date", locale);
  if (dayjs(from).isSame(to, "day")) return formatUiDate(from, "date", locale);
  const startStyle: UiDateStyle = from.getFullYear() === to.getFullYear() ? "dayMonth" : "date";
  return `${formatUiDate(from, startStyle, locale)}${separator}${formatUiDate(to, "date", locale)}`;
};
